/* ═══════════════════════════════════════════════════════
   SwipeHome — State Management Module
   State object, constants, feed persistence, geolocation
   ═══════════════════════════════════════════════════════ */

import type { AppState, UserLocation } from './types/ui.js';

// ─── Constants ──────────────────────────────────────────
export const TOAST_DURATION_MS = 3000;
export const TOAST_ERROR_DURATION_MS = 6000;
export const SWIPE_ANIMATION_DELAY_MS = 200;
export const RADIUS_OPTIONS: readonly number[] = [5, 10, 15, 25, 50];
export const LEAD_TIER_HOT = 70;
export const LEAD_TIER_WARM = 45;
export const LEAD_TIER_COOL = 25;

export const TIER_COLORS: Record<string, string> = {
  hot: 'var(--danger)',
  warm: 'var(--warning)',
  cool: 'var(--primary)',
  cold: 'var(--text-secondary)',
};
export const TIER_BG: Record<string, string> = {
  hot: 'var(--danger-bg)',
  warm: 'var(--warning-bg)',
  cool: 'var(--primary-bg)',
  cold: 'var(--surface-alt)',
};
export const TIER_EMOJI: Record<string, string> = {
  hot: '\uD83D\uDD25',
  warm: '\u2600\uFE0F',
  cool: '\u2744\uFE0F',
  cold: '',
};
export const TIER_LABELS: Record<string, string> = {
  hot: 'HOT',
  warm: 'WARM',
  cool: 'COOL',
  cold: 'COLD',
};

// ─── State ──────────────────────────────────────────────
// Single mutable global. Every module imports and mutates directly.
// The AppState interface catches typos + wrong-type assignments; the
// mutation pattern is preserved from the vanilla JS version so zero
// behavior changes during the migration.
export const state: AppState = {
  token: null,
  user: null,
  role: null,
  currentScreen: 'gateKey',
  screenStack: [],
  feed: [],
  feedIndex: 0,
  likes: [],
  notifications: [],
  clients: [],
  myListings: [],
  lookupResults: [],
  lookupSources: [],
  preferences: null,
  selectedListing: null,
  unreadCount: 0,
  userLocation: null,
  locationDenied: false,
  feedRadius: 25,
  locationRequested: false,
  onboardingStep: 1,
  onboardingData: {
    lat: null,
    lng: null,
    locationText: '',
    minPrice: null,
    maxPrice: null,
    beds: null,
    baths: null,
    propertyTypes: [],
    hoaAllowed: true,
  },
  searchFilters: {
    location: '',
    minPrice: null,
    maxPrice: null,
    beds: null,
    maxBeds: null,
    baths: null,
    maxBaths: null,
    propertyTypes: [],
    hoa: 'any',
    // Investor-POV fix #2: feed defaults to "investment score desc" so the
    // first thing an investor sees is the deal-ranked feed. Buyer-style
    // sorts (newest, price) are still available in the filter drawer.
    sort: 'score',
    radius: 25,
    // Feature 3 advanced filters
    minSqft: null,
    maxSqft: null,
    minYearBuilt: null,
    maxYearBuilt: null,
    hoaMax: null,
    keywords: '',
    priceReduced: false,
    hasOpenHouse: false,
    // Investor pivot — numeric deal filters
    invMinCapRate: null,
    invMinCashOnCash: null,
    invMinDscr: null,
    invMinScore: null,
  },
  searchActive: false,
  feedRestoring: false,
  feedMode: 'for_you',
  feeds: {
    for_you: { items: [], index: 0, loaded: false },
    new: { items: [], index: 0, loaded: false },
    off_market: { items: [], index: 0, loaded: false },
  },
  sessionSwipeCount: 0,
  hasSeenSwipeTutorial: false,
  hasSeenDnaBuildingMsg: false,
  hasSeenSwipeCelebration: false,
  onboardingProgressDismissed: false,
  feedViewMode: 'swipe',
  likesViewMode: 'list',
  compareMode: false,
  compareListingIds: [],
};

// ─── Feed Persistence ─────────────────────────────────
export function saveFeedState(): void {
  try {
    sessionStorage.setItem('swipehome_filters', JSON.stringify(state.searchFilters));
    sessionStorage.setItem('swipehome_listings', JSON.stringify(state.feed));
    sessionStorage.setItem('swipehome_feed_index', String(state.feedIndex));
    sessionStorage.setItem('swipehome_search_active', state.searchActive ? '1' : '0');
    sessionStorage.setItem('swipehome_feed_mode', state.feedMode);
    // View-mode preferences live in localStorage (Feature 4c onward)
    // so they persist across sessions, not just tab reloads.
    localStorage.setItem('swipehome_feed_view_mode', state.feedViewMode);
    localStorage.setItem('swipehome_likes_view_mode', state.likesViewMode);
  } catch {
    /* storage full or unavailable */
  }
}

/**
 * Feature 4c — restore view-mode preferences from localStorage on
 * boot. Called from main.ts at startup (before the first render) so
 * the feed and likes screens come up in the user's last-used view.
 * Migrates legacy sessionStorage keys transparently.
 */
export function restoreViewPrefs(): void {
  try {
    let savedFeed = localStorage.getItem('swipehome_feed_view_mode');
    if (!savedFeed) {
      // Legacy: Feature 4a/4b used sessionStorage with a two-value
      // enum where 'list' meant "swipe stack". Migrate once.
      const legacy = sessionStorage.getItem('swipehome_feed_view');
      if (legacy === 'map') savedFeed = 'map';
      else if (legacy === 'list') savedFeed = 'swipe';
    }
    // Feature 4e collapsed 'list' into the map drawer; legacy
    // 'list' preference migrates to 'map' so returning users land
    // on the combined view.
    if (savedFeed === 'swipe' || savedFeed === 'map') {
      state.feedViewMode = savedFeed;
    } else if (savedFeed === 'list') {
      state.feedViewMode = 'map';
    }
    const savedLikes = localStorage.getItem('swipehome_likes_view_mode')
      ?? sessionStorage.getItem('swipehome_likes_view');
    if (savedLikes === 'list' || savedLikes === 'map') {
      state.likesViewMode = savedLikes;
    }
  } catch {
    /* storage unavailable — stay with defaults */
  }
}

export function restoreFeedState(): boolean {
  try {
    const filters = sessionStorage.getItem('swipehome_filters');
    const listings = sessionStorage.getItem('swipehome_listings');
    const index = sessionStorage.getItem('swipehome_feed_index');
    const active = sessionStorage.getItem('swipehome_search_active');
    if (filters) {
      // Merge over defaults so a snapshot from a pre-Feature-3 client
      // (missing minSqft, priceReduced, etc.) still leaves the new
      // fields at their default values instead of undefined.
      const parsedFilters = JSON.parse(filters);
      state.searchFilters = { ...state.searchFilters, ...parsedFilters };
    }
    if (listings) {
      const parsed = JSON.parse(listings);
      if (Array.isArray(parsed) && parsed.length > 0) {
        state.feed = parsed;
        state.feedIndex = index ? parseInt(index, 10) : 0;
        state.searchActive = active === '1';
        const savedMode = sessionStorage.getItem('swipehome_feed_mode');
        if (savedMode === 'for_you' || savedMode === 'new' || savedMode === 'off_market') state.feedMode = savedMode;
        // Feature 4c: view-mode prefs now live in localStorage and
        // are restored separately in restoreViewPrefs() at boot.
        // This path just handles the feed data + pagination index.
        return true;
      }
    }
  } catch {
    /* corrupted data, ignore */
  }
  return false;
}

export function clearFeedState(): void {
  sessionStorage.removeItem('swipehome_filters');
  sessionStorage.removeItem('swipehome_listings');
  sessionStorage.removeItem('swipehome_feed_index');
  sessionStorage.removeItem('swipehome_search_active');
}

// ─── Geolocation ──────────────────────────────────────
export function getUserLocation(): Promise<UserLocation | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 10000, enableHighAccuracy: false },
    );
  });
}
